import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, useApi } from "@/lib/api";

function ExistingLead({ onPick, busy }) {
  const { data } = useApi("/leads");
  const [q, setQ] = useState("");
  const rows = useMemo(() => (data || []).filter((l) => !l.projectId
    && `${l.clientName} ${l.clientPhone} ${l.leadNumber}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8), [data, q]);
  return (
    <div className="space-y-3">
      <Input autoFocus placeholder="Search name, phone or lead number" value={q} onChange={(e) => setQ(e.target.value)} data-testid="assign-search-input" />
      <div className="max-h-72 overflow-y-auto divide-y rounded-md border" data-testid="assign-lead-results">
        {!data ? <div className="p-4 text-sm text-muted-foreground">Loading leads…</div> : !rows.length ? <div className="p-4 text-sm text-muted-foreground">No matching leads.</div>
          : rows.map((l) => (
            <button key={l.id} disabled={busy} onClick={() => onPick({ leadId: l.id })} className="w-full text-left px-3 py-2.5 hover:bg-secondary/50 transition-colors duration-75" data-testid={`assign-lead-${l.leadNumber}`}>
              <div className="text-sm font-medium">{l.clientName}</div>
              <div className="text-xs text-muted-foreground">{l.leadNumber} · {l.clientPhone}{l.clientCity ? ` · ${l.clientCity}` : ""}</div>
            </button>))}
      </div>
    </div>
  );
}

function NewLead({ onPick, busy }) {
  const [f, setF] = useState({ clientName: "", clientPhone: "", clientCity: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); onPick({ newLead: f }); }}>
      <div className="space-y-1.5"><Label>Client name</Label><Input required value={f.clientName} onChange={set("clientName")} data-testid="assign-new-name-input" /></div>
      <div className="space-y-1.5"><Label>Phone</Label><Input required value={f.clientPhone} onChange={set("clientPhone")} data-testid="assign-new-phone-input" /></div>
      <div className="space-y-1.5"><Label>City</Label><Input value={f.clientCity} onChange={set("clientCity")} data-testid="assign-new-city-input" /></div>
      <Button type="submit" disabled={busy} className="w-full" data-testid="assign-new-submit">Create lead & attach quote</Button>
    </form>
  );
}

export function AssignClientDialog({ quote, open, onClose, onAssigned }) {
  const [busy, setBusy] = useState(false);
  const pick = async (body) => {
    setBusy(true);
    try {
      const r = await api.post(`/quotations/${quote.id}/assign`, body);
      toast.success(`Quote attached as ${r.data.quoteNo}`);
      onAssigned(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-client-dialog">
        <DialogHeader><DialogTitle>Assign to client / lead</DialogTitle>
          <DialogDescription>{quote.quoteNo} gets a regular quotation number once it's attached.</DialogDescription></DialogHeader>
        <Tabs defaultValue="existing">
          <TabsList className="grid grid-cols-2 w-full"><TabsTrigger value="existing" data-testid="assign-tab-existing">Existing lead</TabsTrigger>
            <TabsTrigger value="new" data-testid="assign-tab-new">New lead</TabsTrigger></TabsList>
          <TabsContent value="existing" className="pt-3"><ExistingLead onPick={pick} busy={busy} /></TabsContent>
          <TabsContent value="new" className="pt-3"><NewLead onPick={pick} busy={busy} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
